import React, { useEffect, useRef, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useQuery } from "@apollo/client";
import {
  LISTAR_ESTADOS,
  LISTAR_CIDADES,
  Estado,
  Cidade,
} from "../services/localizacaoService";
import { StatusTorneio } from "../utils/StatusTorneio";

export interface TournamentFilters {
  cidade?: Cidade | null;
  data?: string;
  status?: string;
  verEncerrados?: boolean;
}

export interface TournamentFilterModalProps {
  open: boolean;
  onClose: () => void;
  onFilter: (filters: TournamentFilters) => void;
}

const TournamentFilterModal: React.FC<TournamentFilterModalProps> = ({
  open,
  onClose,
  onFilter,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { data: estadosData } = useQuery<{ estados: Estado[] }>(LISTAR_ESTADOS);
  const { data: cidadesData } = useQuery<{ cidades: Cidade[] }>(LISTAR_CIDADES);

  const statusTorneioOptions = Object.values(StatusTorneio).map((status) => ({
    value: status,
    label: status
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/^\w|\s\w/g, (c) => c.toUpperCase()),
  }));

  const [estado, setEstado] = useState<Estado | null>(null);
  const [cidade, setCidade] = useState<Cidade | null>(null);
  const [data, setData] = useState("");
  const [status, setStatus] = useState("");
  const [verEncerrados, setVerEncerrados] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.classList.add("modal-open");
      modalRef.current?.focus();
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("modal-open");
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleApply = () => {
    onFilter({ cidade, data, status, verEncerrados });
    onClose();
  };

  const handleReset = () => {
    setCidade(null);
    setData("");
    setStatus("");
    setVerEncerrados(false);
    onFilter({});
    onClose();
  };

  return (
    <>
      <div
        className="modal-backdrop fade show"
        onClick={onClose}
        style={{ position: "fixed", inset: 0, zIndex: 1050 }}
      />
      <div
        className="modal fade show d-block"
        role="dialog"
        tabIndex={-1}
        ref={modalRef}
        onMouseDown={handleBackdrop}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1055,
          overflow: "auto",
        }}
      >
        <div
          className="modal-dialog"
          role="document"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title w-100 text-center">Filtros</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Fechar"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3 form-group">
                <label className="mont-font fw-600 font-xsss mb-2">
                  Estado
                </label>
                <Autocomplete
                  id="estado"
                  options={estadosData?.estados || []}
                  getOptionLabel={(option: Estado) => option.nome}
                  value={estado}
                  onChange={(_, newValue) => {
                    setEstado(newValue);
                    setCidade(null);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Selecione o estado"
                    />
                  )}
                />
              </div>
              <div className="mb-3 form-group">
                <label className="form-label d-block">Cidade</label>
                <Autocomplete
                  id="filtro-cidade"
                  options={cidadesData?.cidades ? cidadesData.cidades.filter(cidade => cidade.estado.id === estado?.id) : []}
                  getOptionLabel={(option: Cidade) => option.nome}
                  value={cidade}
                  onChange={(_, val) => setCidade(val)}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Selecione ou digite a cidade"
                    />
                  )}
                />
              </div>
              <div className="mb-3">
                <label className="form-label d-block">Data</label>
                <input
                  type="date"
                  className="form-control"
                  value={data}
                  onChange={(e) => setData(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <label className="form-label d-block">Status</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">Todos</option>
                  {statusTorneioOptions.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="filtro-ver-encerrados"
                  checked={verEncerrados}
                  onChange={(e) => setVerEncerrados(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="filtro-ver-encerrados"
                >
                  Ver torneios encerrados
                </label>
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-link font-xsss"
                style={{ color: "#3B3B3B" }}
                onClick={handleReset}
              >
                Redefinir filtros
              </button>
              <button
                type="button"
                className="btn btn-primary font-xsss lh-38 py-2"
                onClick={handleApply}
              >
                FILTRAR
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TournamentFilterModal;
